/**
 * useWarRoomOverview.ts - Beta 3 War Room
 * 
 * Bulk fetch hook for user entries across multiple pools.
 * Cannot use useUserEntry in a loop, so this fetches entries in parallel.
 * 
 * @see WAR_ROOM_DEV_PLAN.md Section 3.3
 */

import { useState, useEffect, useCallback } from 'react';

// =============================================================================
// Types
// =============================================================================

export interface EntryRosterSlot {
  slot: number;
  label: string;
  player_prop_id: number | null;
  side: 'OVER' | 'UNDER' | null;
  units: number;
  player?: {
    id: number;
    name: string;
    team: string;
    pos: string;
  } | null;
  prop?: {
    id: number;
    stat: string;
    line: number;
  } | null;
}

export interface EntryData {
  portfolioId: string;
  poolId: string;
  userId: string;
  status: 'not_entered' | 'in_progress' | 'ready' | 'locked' | 'completed';
  isLocked: boolean;
  lockedAt?: string;
  totalUnits: number;
  filledSlots: number;
  roster: EntryRosterSlot[];
  score?: number;
  rank?: number;
}

export interface UseWarRoomOverviewResult {
  entries: Map<string, EntryData | null>;
  loading: boolean;
  error: string | null;
  refresh: () => void;
}

// =============================================================================
// Hook
// =============================================================================

const API_BASE = import.meta.env.VITE_API_BASE_URL || '/api/v1';

/**
 * Fetch entries for multiple pools in parallel.
 * 
 * - Uses Promise.all for parallel fetching
 * - 404 responses are treated as empty roster (not an error)
 * - Individual failures don't block other pools from loading
 * 
 * @param userId - Current user's ID
 * @param poolIds - Array of pool_id strings to fetch entries for
 */
export function useWarRoomOverview(
  userId: string | null,
  poolIds: string[]
): UseWarRoomOverviewResult {
  const [entries, setEntries] = useState<Map<string, EntryData | null>>(new Map());
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchEntries = useCallback(async () => {
    // Guard: No user or no pools
    if (!userId || userId === 'guest' || poolIds.length === 0) {
      setEntries(new Map());
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      // Fetch all entries in parallel
      const fetchPromises = poolIds.map(async (poolId) => {
        try {
          const response = await fetch(`${API_BASE}/pools/${poolId}/entry`, {
            headers: {
              'X-User-ID': userId,
            },
          });

          if (response.status === 404) {
            // No entry yet - return empty roster structure
            return {
              poolId,
              entry: {
                portfolioId: '',
                poolId,
                userId,
                status: 'not_entered' as const,
                isLocked: false,
                totalUnits: 0,
                filledSlots: 0,
                roster: [],
              },
            };
          }

          if (!response.ok) {
            // Other error - return null for this pool
            console.warn(`Failed to fetch entry for pool ${poolId}: ${response.status}`);
            return { poolId, entry: null };
          }

          const data = await response.json();
          return { poolId, entry: data as EntryData };
        } catch (err) {
          // Network error - return null for this pool (non-blocking)
          console.warn(`Error fetching entry for pool ${poolId}:`, err);
          return { poolId, entry: null };
        }
      });

      const results = await Promise.all(fetchPromises);

      // Build the map
      const newEntries = new Map<string, EntryData | null>();
      results.forEach(({ poolId, entry }) => {
        newEntries.set(poolId, entry);
      });

      setEntries(newEntries);
    } catch (err) {
      // Catastrophic error (shouldn't happen with Promise.all pattern)
      setError(err instanceof Error ? err.message : 'Failed to load entries');
    } finally {
      setLoading(false);
    }
  }, [userId, poolIds.join(',')]); // Join for stable dependency

  useEffect(() => {
    fetchEntries();
  }, [fetchEntries]);

  return {
    entries,
    loading,
    error,
    refresh: fetchEntries,
  };
}

export default useWarRoomOverview;
